import dayjs from 'dayjs';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import 'dayjs/locale/hu';

import { dayjsLocale } from './locale';
import enTranslations from './locales/en.json';
import huTranslations from './locales/hu.json';
import srTranslations from './locales/sr.json';

const resources = {
  hu: {
    translation: huTranslations,
  },
  en: {
    translation: enTranslations,
  },
  sr: {
    translation: srTranslations,
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // Per-language fallback chains, deliberately not a single string.
    //
    // `sr` falls back to Hungarian first: the Serbian dictionary is partial,
    // and the pages it lacks have always rendered in Hungarian for Serbian
    // admins. Routing them to English instead would silently change the
    // interface of a live customer, so Hungarian stays first in the chain.
    //
    // `sr.json` is deliberately held to exactly the keys that render in
    // Serbian today — the menu, the login page, and the shared labels on
    // Registrations and Check-in. Everything else falls through to Hungarian.
    // That is not neglect: converting a hardcoded Hungarian screen to i18next
    // would otherwise flip it to Serbian the moment a stale translation
    // happened to exist for one of its keys, half-translating a live panel
    // without anyone deciding to. Do not add Serbian values one screen at a
    // time; a Serbian UI is a deliberate, whole-panel piece of work.
    //
    // Everything else falls back to English. That covers the case the chain
    // exists for: a browser reporting a language we have no dictionary for
    // (e.g. `nl-NL`) previously landed on Hungarian for *every* key, which
    // meant a Dutch organizer met a Hungarian admin panel on first login.
    fallbackLng: {
      sr: ['hu', 'en'],
      default: ['en'],
    },
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

// dayjs holds ONE global locale, and it decides every `MMM`/`ddd` format token
// and every bare `fromNow()` in the panel. Two screens used to pin it to `hu` at
// import time, which was right while the admin was Hungarian only; once the
// panel could render English it printed Hungarian month names under English
// labels. It belongs here rather than in a React effect: this runs at module
// evaluation and on i18next's own event, so neither the first paint nor the
// render right after a language switch is left on the previous locale.
//
// `dayjsLocale` sends Serbian to `hu`, the language its text falls back to, so
// the Serbian output does not move. Only `hu` needs loading — `en` is built in.
const syncDayjsLocale = (language?: string) => {
  dayjs.locale(dayjsLocale(language ?? i18n.language));
};

syncDayjsLocale();
i18n.on('languageChanged', syncDayjsLocale);

export default i18n;
